import React, { useEffect, useRef } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Check, Settings, Image as ImageIcon, ArrowRight } from 'lucide-react';
import { PRODUCTS } from '../constants';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';

export const ProductDetail = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  const { productId } = useParams<{ productId: string }>();
  const product = PRODUCTS.find(p => p.id === productId);
  const currentIndex = PRODUCTS.findIndex(p => p.id === productId);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | QuikPix NZ`;
      window.scrollTo(0, 0);
    }
  }, [product]);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.scrollY;
        // Parallax effect
        parallaxRef.current.style.transform = `translateY(${scrolled * 0.4}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!product) {
    return <Navigate to="/" replace />;
  }

  // Calculate the next 3 products to show in the loop
  const otherProducts = [];
  if (currentIndex !== -1) {
    for (let i = 1; i <= 3; i++) {
      otherProducts.push(PRODUCTS[(currentIndex + i) % PRODUCTS.length]);
    }
  }

  return (
    <div className="min-h-screen">
      {/* Product Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div ref={parallaxRef} className="absolute inset-0 z-0 will-change-transform">
           <img 
            src={product.image}
            className="w-full h-full object-cover opacity-40" 
            alt={product.name}
          />
        </div>
        
        {/* Static gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/40 z-0"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <div className="flex justify-center gap-2 mb-6 animate-fade-in-up">
             {product.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-brand-neonBlue/10 border border-brand-neonBlue text-brand-neonBlue text-xs font-bold uppercase tracking-wider rounded-full">
                  {tag}
                </span>
             ))}
          </div>
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            {product.name}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {product.description}
          </p>
          <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Button size="lg" onClick={onOpenQuote}>
              Get a Quote for {product.name}
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-24">
        
        {/* Overview & Specs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-display font-bold text-white mb-6">The Experience</h2>
            <p className="text-slate-300 leading-relaxed text-lg mb-8">
              {product.longDescription}
            </p>
            
            <h3 className="text-xl font-bold text-white mb-4">Key Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.features.map((feature, idx) => (
                <GlassCard key={idx} className="flex items-center gap-3 p-4">
                  <div className="w-8 h-8 rounded-full bg-brand-neonBlue/20 flex items-center justify-center text-brand-neonBlue shrink-0">
                    <Check size={16} />
                  </div>
                  <span className="text-slate-200">{feature}</span>
                </GlassCard>
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <GlassCard className="sticky top-24 border-brand-neonPink/20">
              <div className="flex items-center gap-2 mb-6 text-brand-neonPink">
                <Settings size={24} />
                <h3 className="text-xl font-bold">Tech Specs</h3>
              </div>
              <div className="space-y-4">
                {Object.entries(product.specs).map(([key, value]) => (
                  <div key={key} className="border-b border-white/5 pb-3 last:border-0 last:pb-0">
                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">{key}</p>
                    <p className="text-white font-medium">{value}</p>
                  </div>
                ))}
              </div>
              <Button variant="outline" className="w-full mt-8" onClick={onOpenQuote}>
                Download PDF Guide
              </Button>
            </GlassCard>
          </div>
        </div>

        {/* Gallery - Bento Grid */}
        <section>
          <div className="flex items-center gap-2 mb-8">
            <ImageIcon className="text-slate-400" />
            <h2 className="text-3xl font-display font-bold text-white">Gallery</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[250px] gap-4">
            {product.gallery.map((img, idx) => {
              // Bento Layout Logic:
              // Index 0: Large feature item (2x2) - Good for Landscape/Square
              // Index 1: Tall item (1x2) - Good for Portrait
              // Index 2, 3, 4: Standard items (1x1)
              let spanClass = "md:col-span-1 md:row-span-1";
              if (idx === 0) spanClass = "md:col-span-2 md:row-span-2";
              else if (idx === 1) spanClass = "md:col-span-1 md:row-span-2";

              return (
                <div key={idx} className={`relative rounded-2xl overflow-hidden group border border-white/10 ${spanClass}`}>
                  <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                  <img 
                    src={img.url} 
                    alt={img.caption || `${product.name} gallery ${idx + 1}`} 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-slate-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                     <span className="text-white text-xs font-bold uppercase tracking-widest">{img.caption || 'View Image'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-r from-brand-neonBlue/10 to-brand-neonPink/10 rounded-2xl p-12 text-center border border-white/10">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">Ready to book {product.name}?</h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">Dates fill up fast, especially for weekends. Secure your hardware today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={onOpenQuote}>Check Availability</Button>
            <Button variant="outline" size="lg">Contact Us</Button>
          </div>
        </div>

        {/* Related Products Loop */}
        <section className="pt-12 border-t border-white/5">
           <div className="flex items-center justify-between mb-8">
             <h2 className="text-2xl font-display font-bold text-white">Explore More Experiences</h2>
             <Link to="/" className="text-brand-neonBlue hover:text-white transition-colors text-sm font-medium flex items-center gap-1">
               View All <ArrowRight size={16} />
             </Link>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {otherProducts.map((p) => (
               <Link to={`/product/${p.id}`} key={p.id} className="group block">
                 <GlassCard hoverEffect className="h-full p-0 overflow-hidden bg-slate-900/50">
                    <div className="aspect-video overflow-hidden">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <div className="flex gap-2 mb-2">
                        {p.tags.slice(0, 1).map(tag => (
                          <span key={tag} className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-white/5 text-slate-300 border border-white/10">{tag}</span>
                        ))}
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-brand-neonBlue transition-colors">{p.name}</h3>
                      <p className="text-sm text-slate-400 line-clamp-2">{p.description}</p>
                    </div>
                 </GlassCard>
               </Link>
             ))}
           </div>
        </section>

      </div>
    </div>
  );
};